import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { useModalCloseEffect } from '../../hooks/useModalCloseEffect';
import { cn } from '../../utils/cn.utils';

export const DrawerVariants = cva(
  'absolute bg-white shadow-lg flex flex-col', // (기본 스타일: h-full, max-w-full 제거)
  {
    variants: {
      placement: {
        right: 'top-0 right-0',
        left: 'top-0 left-0',
        top: 'top-0 left-0 w-full', // w-full 추가
        bottom: 'bottom-0 left-0 w-full', // w-full 추가
      },
      size: {
        sm: '',
        md: '',
        lg: '',
      },
    },
    compoundVariants: [
      { placement: 'left', size: 'sm', className: 'w-[300px] h-full' },
      { placement: 'left', size: 'md', className: 'w-[400px] h-full' },
      { placement: 'left', size: 'lg', className: 'w-[500px] h-full' },
      { placement: 'right', size: 'sm', className: 'w-[300px] h-full' },
      { placement: 'right', size: 'md', className: 'w-[400px] h-full' },
      { placement: 'right', size: 'lg', className: 'w-[500px] h-full' },

      // Top/Bottom: size가 height를 제어 (h 값은 예시입니다)
      { placement: 'top', size: 'sm', className: 'h-[200px]' },
      { placement: 'top', size: 'md', className: 'h-[300px]' },
      { placement: 'top', size: 'lg', className: 'h-[400px]' },
      { placement: 'bottom', size: 'sm', className: 'h-[200px]' },
      { placement: 'bottom', size: 'md', className: 'h-[300px]' },
      { placement: 'bottom', size: 'lg', className: 'h-[400px]' },
    ],
    defaultVariants: {
      placement: 'right',
      size: 'md',
    },
  }
);

// [3. Motion 수정] top, bottom 애니메이션 추가
const motionVariants = {
  left: {
    initial: { x: '-100%' },
    animate: { x: 0 },
    exit: { x: '-100%' },
  },
  right: {
    initial: { x: '100%' },
    animate: { x: 0 },
    exit: { x: '100%' },
  },
  top: {
    initial: { y: '-100%' }, // y축으로 변경
    animate: { y: 0 },
    exit: { y: '-100%' },
  },
  bottom: {
    initial: { y: '100%' }, // y축으로 변경
    animate: { y: 0 },
    exit: { y: '100%' },
  },
};

interface Props
  extends React.ComponentProps<'div'>,
  VariantProps<typeof DrawerVariants> {
  enableOutsideClick?: boolean;
  children?: React.ReactNode;
  close: () => void;
}

export default function Drawer({
  size,
  placement = 'right', // (기본값)
  close,
  enableOutsideClick = true,
  className,
  children,
}: Props) {
  const { wrapperRef, handleOutsideClick } = useModalCloseEffect({
    onClose: close,
    enableOutsideClick: enableOutsideClick,
  });

  const selectedMotion = motionVariants[placement!];

  return (
    <AnimatePresence>
      <motion.div
        ref={wrapperRef}
        onClick={handleOutsideClick}
        className="fixed inset-0 z-1300 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={selectedMotion.initial}
          animate={selectedMotion.animate}
          exit={selectedMotion.exit}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className={cn(DrawerVariants({ size, placement }), className)}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}